import { useMemo } from 'react';
import { useGetMetricDefinitionsQuery } from 'src/features/api/metricDefinitionsApi';
import { DERIVED_OTD_FAMILIES } from 'src/features/api/tableSelectors';
import { WBR_REVIEW_TYPE } from 'src/constants';

export interface MetricCatalogEntry {
  name: string;
  group: string;
}

/**
 * The dashboard's metric catalog keyed by metricKey, with derived OTD rows
 * mapped onto their carrier keys. Shared by every surface that turns a
 * metricKey back into its display name.
 */
export function useMetricCatalog(dashboardId: string) {
  const { data } = useGetMetricDefinitionsQuery({
    dashboardId,
    reviewType: WBR_REVIEW_TYPE,
  });
  const catalog = useMemo(() => {
    const map = new Map<string, MetricCatalogEntry>();
    for (const d of data?.definitions ?? [])
      map.set(d.metricKey, { name: d.name, group: d.metricGroup });
    for (const family of DERIVED_OTD_FAMILIES) {
      const carrier = map.get(family.carrierKey);
      map.set(family.carrierKey, {
        name: family.derivedRow,
        group: carrier?.group ?? '',
      });
    }
    return map;
  }, [data]);

  const nameOf = (key: string) => catalog.get(key)?.name ?? key;
  const groupOf = (key: string) => catalog.get(key)?.group ?? '';
  return { catalog, nameOf, groupOf, definitionsResponse: data };
}
